import { useEffect, useState } from 'react';
import { fetchVendors, updateUser, deleteUser } from '../services/api';
import { DotsThreeVertical, Pencil, Trash } from "phosphor-react";

const Vendors = () => {
  const [vendors, setVendors] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', balance: '' });
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [isLoading, setIsLoading] = useState({ edit: false, delete: false, initialLoad: true });
  const [errors, setErrors] = useState({ name: '', balance: '' });
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewUser, setViewUser] = useState(null);

  useEffect(() => {
    const loadVendors = async () => {
      try {
        const data = await fetchVendors();
        setVendors(data);
      } catch (error) {
        showNotification('Error loading Vendors: ' + error.message, 'error');
      } finally {
        setIsLoading(prev => ({ ...prev, initialLoad: false }));
      }
    };
    loadVendors();
  }, []);

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification(prev => ({ ...prev, show: false })), 2000);
  };

  const filteredVendors = vendors.filter(user =>
    (user.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (user.email?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const indexOfLastRecord = currentPage * recordsPerPage;
  const paginatedVendors = filteredVendors.slice(indexOfLastRecord - recordsPerPage, indexOfLastRecord);
  const totalPages = Math.ceil(filteredVendors.length / recordsPerPage);

  const handleEditClick = (user) => {
    setCurrentUser(user);
    setFormData({ name: user.name, email: user.email, balance: user.balance.toString() });
    setErrors({ name: '', balance: '' });
    setIsModalOpen(true);
    document.body.classList.add('modal-open');
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setIsDeleteConfirmOpen(true);
    document.body.classList.add('modal-open');
  };

  const closeAllModals = () => {
    setIsModalOpen(false);
    setIsDeleteConfirmOpen(false);
    setIsViewModalOpen(false);
    setViewUser(null);
    document.body.classList.remove('modal-open');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateName = (name) => {
    if (!name.trim()) return 'Name is required';
    if (name.length < 2) return 'Name must be at least 2 characters';
    if (name.length > 50) return 'Name cannot exceed 50 characters';
    return '';
  };

  const validateBalance = (balance) => {
    if (balance === '') return 'Balance is required';
    if (isNaN(parseFloat(balance))) return 'Must be a valid number';
    if (parseFloat(balance) < 0) return 'Cannot be negative';
    return '';
  };

  const validateForm = () => {
    const nameError = validateName(formData.name);
    const balanceError = validateBalance(formData.balance);
    setErrors({ name: nameError, balance: balanceError });
    return !nameError && !balanceError;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(prev => ({ ...prev, edit: true }));
    try {
      const updatedData = { name: formData.name, balance: parseFloat(formData.balance) };
      const response = await updateUser(currentUser.id, updatedData);
      setVendors(prev => prev.map(u => u.id === currentUser.id ? { ...u, ...updatedData } : u));
      closeAllModals();
      showNotification(response.message || 'User updated successfully!');
    } catch (error) {
      showNotification('Error updating user: ' + error.message, 'error');
    } finally {
      setIsLoading(prev => ({ ...prev, edit: false }));
    }
  };

  const confirmDelete = async () => {
    setIsLoading(prev => ({ ...prev, delete: true }));
    try {
      await deleteUser(userToDelete.id);
      setVendors(vendors.filter(u => u.id !== userToDelete.id));
      closeAllModals();
      showNotification('User deleted successfully!');
    } catch (error) {
      showNotification('Error deleting user: ' + error.message, 'error');
    } finally {
      setIsLoading(prev => ({ ...prev, delete: false }));
    }
  };
  const handleViewClick = (user) => {
    setViewUser(user);
    setIsViewModalOpen(true);
    document.body.classList.add('modal-open');
  };


  return (
    <div className="position-relative">
      {/* Notification */}
      {notification.show && (
        <div className={`alert alert-${notification.type === 'success' ? 'success' : 'danger'} position-fixed top-0 end-0 m-3 shadow`}>
          {notification.message}
        </div>
      )}

      {/* Table */}
      <div className="theme-card-white">
        <div className="card-body">
          <div className="theme-card-header">
            <h5 className="card-head">Vendors</h5>
            <input
              type="text"
              className="form-control form-control-300 w-auto"
              placeholder="Search Vendors"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {isLoading.initialLoad ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <div className="spinner-border text-primary" role="status"></div>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table theme-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Vendor</th>
                      <th>Email</th>
                      <th>Contact Number</th>
                      <th>Balance</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                 <tbody>
                    {paginatedVendors.length > 0 ? (
                      paginatedVendors.map((user) => (
                        <tr key={user.id}>
                          <td>{user.id}</td>
                          <td>{user.name}</td>
                          <td>{user.email}</td>
                          <td>{user.contact_number}</td>
                          <td>
                            <span className="badge bg-success text-success">
                              ${parseFloat(user.balance).toFixed(2)}
                            </span>
                          </td>
                          <td>
                            <div className="dropdown">
                              <button
                                className="theme-drop"
                                type="button"
                                data-bs-toggle="dropdown"
                              >
                                <DotsThreeVertical size={18} weight="bold" />
                              </button>
                              <ul className="dropdown-menu dropdown-menu-end">
                                <li>
                                  <button
                                    className="dropdown-item"
                                    onClick={() => handleEditClick(user)}
                                  >
                                    <Pencil size={16} /> Edit
                                  </button>
                                </li>
                                <li>
                                  <button
                                    className="dropdown-item text-danger"
                                    onClick={() => handleDeleteClick(user)}
                                  >
                                    <Trash size={16} /> Delete
                                  </button>
                                </li>
                                <li>
                                  <button
                                    className="dropdown-item"
                                    onClick={() => handleViewClick(user)}
                                  >
                                    👁 View
                                  </button>
                                </li>
                              </ul>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center py-4 text-muted">
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              <nav className="d-flex justify-content-end">
                <ul className="pagination mb-0">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>
                      Prev
                    </button>
                  </li>
                  {[...Array(totalPages)].map((_, i) => (
                    <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(i + 1)}>
                        {i + 1}
                      </button>
                    </li>
                  ))}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {isModalOpen && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Vendor</h5>
                  <button type="button" className="btn-close" onClick={closeAllModals}></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Name</label>
                      <input
                        type="text"
                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                      />
                      {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Email</label>
                      <input type="email" className="form-control" value={formData.email} disabled />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Balance</label>
                      <input
                        type="number"
                        className={`form-control ${errors.balance ? 'is-invalid' : ''}`}
                        name="balance"
                        value={formData.balance}
                        onChange={handleInputChange}
                      />
                      {errors.balance && <div className="invalid-feedback">{errors.balance}</div>}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="border-btn" onClick={closeAllModals}>
                      Cancel
                    </button>
                    <button type="submit" className="theme-btn" disabled={isLoading.edit}>
                      {isLoading.edit ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {/* Delete Modal */}
      {isDeleteConfirmOpen && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Delete</h5>
                  <button type="button" className="btn-close" onClick={closeAllModals}></button>
                </div>
                <div className="modal-body">
                  <p className='text-sm font-400 theme-text-color'>Are you sure you want to delete <strong>{userToDelete?.name}</strong>?</p>
                  <p className="text-muted small mb-0">This action cannot be undone.</p>
                </div>
                <div className="modal-footer">
                  <button className="border-btn" onClick={closeAllModals}>
                    Cancel
                  </button>
                  <button className="theme-btn" onClick={confirmDelete} disabled={isLoading.delete}>
                    {isLoading.delete ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Deleting...
                      </>
                    ) : (
                      'Delete'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {isViewModalOpen && viewUser && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Vendor Details</h5>
                  <button className="btn-close" onClick={closeAllModals}></button>
                </div>

                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-8">
                      <p><strong>Name:</strong> {viewUser.name}</p>
                      <p><strong>Email:</strong> {viewUser.email}</p>
                      <p><strong>Contact:</strong> {viewUser.contact_number}</p>
                      <p><strong>Shop Name:</strong> {viewUser.shop_name}</p>
                      <p><strong>Shop Email:</strong> {viewUser.shop_email}</p>
                      <p><strong>Shop Contact:</strong> {viewUser.shop_contact || '-'}</p>
                      <p><strong>Shop Address:</strong> {viewUser.shop_address}</p>
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button className="border-btn" onClick={closeAllModals}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}

    </div>
  );
};

export default Vendors;
